import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Trash2,
  Clock,
  AlertCircle,
  CheckCircle,
  Pause,
  FileText,
  Flag,
  MoreVertical,
  Loader2,
  Map
} from 'lucide-react';

interface SavedWorkCardProps {
  job: {
    id: string;
    business_name: string;
    client_name?: string;
    law_firm?: string;
    bank_name?: string;
    status: string;
    workflow_status?: string;
    auto_status?: string;
    priority?: string;
    notes?: string;
    saved_at?: string;
    created_at?: string;
    updated_at?: string;
    days_inactive?: number;
    doc_count?: number;
    total_transactions?: number;
    needs_review?: number;
    step?: number;
    source?: string;
    stats?: {
      total_transactions?: number;
      auto_approved?: number;
      needs_review?: number;
    };
  };
  onDelete: (id: string) => void;
  onStatusChange?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  draft: {
    label: 'Draft',
    color: 'text-[var(--color-text-muted)]',
    bgColor: 'bg-[var(--color-hairline)]',
    icon: <FileText className="w-3 h-3" />
  },
  awaiting_data: {
    label: 'Awaiting Data',
    color: 'text-[var(--color-text-muted)]',
    bgColor: 'bg-[var(--color-hairline)]',
    icon: <Clock className="w-3 h-3" />
  },
  processing: {
    label: 'Processing',
    color: 'text-[var(--color-needs-human-text)]',
    bgColor: 'bg-[rgba(255,102,0,0.12)]',
    icon: <Loader2 className="w-3 h-3 animate-spin" />
  },
  needs_review: {
    label: 'Needs Review',
    color: 'text-[var(--color-needs-human-text)]',
    bgColor: 'bg-[rgba(255,102,0,0.12)]',
    icon: <AlertCircle className="w-3 h-3" />
  },
  ready: {
    label: 'Ready',
    color: 'text-[var(--color-verified)]',
    bgColor: 'bg-[rgba(34,197,94,0.12)]',
    icon: <CheckCircle className="w-3 h-3" />
  },
  stalled: {
    label: 'Stalled',
    color: 'text-[var(--color-needs-human-text)]',
    bgColor: 'bg-[rgba(255,102,0,0.12)]',
    icon: <Pause className="w-3 h-3" />
  },
  on_hold: {
    label: 'On Hold',
    color: 'text-[var(--color-text-muted)]',
    bgColor: 'bg-[var(--color-hairline)]',
    icon: <Pause className="w-3 h-3" />
  },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; border: string }> = {
  low: { label: 'Low', color: 'text-[var(--color-text-dim)]', border: 'border-l-[var(--color-status-queued)]' },
  medium: { label: 'Medium', color: 'text-[var(--color-text-muted)]', border: 'border-l-[var(--color-status-queued)]' },
  high: { label: 'High', color: 'text-[var(--color-brand)]', border: 'border-l-[var(--color-brand)]' },
  urgent: { label: 'Urgent', color: 'text-[var(--color-needs-human-text)]', border: 'border-l-[var(--color-needs-human-text)]' },
};

export default function SavedWorkCard({ job, onDelete, onStatusChange }: SavedWorkCardProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [localStatus, setLocalStatus] = useState(job.workflow_status || job.auto_status || 'draft');
  const [localPriority, setLocalPriority] = useState(job.priority || 'medium');

  const statusConfig = STATUS_CONFIG[localStatus] || STATUS_CONFIG.draft;
  const priorityConfig = PRIORITY_CONFIG[localPriority] || PRIORITY_CONFIG.medium;

  // Generate short ID from UUID (first 8 chars)
  const shortId = `#${job.id.substring(0, 8).toUpperCase()}`;

  // Calculate progress based on step (wizard has 7 steps)
  const TOTAL_WIZARD_STEPS = 7;
  const progressPercent = job.step ? Math.min((job.step / TOTAL_WIZARD_STEPS) * 100, 100) : 0;

  // Transaction stats
  const totalTxn = job.total_transactions || job.stats?.total_transactions || 0;
  const needsReview = job.needs_review || job.stats?.needs_review || 0;
  const autoApproved = totalTxn - needsReview;

  const handleContinue = async () => {
    if (job.source === 'wizard') {
      try {
        const token = await getToken();
        const draftData = await api.get(`/api/v1/reports/drafts/${job.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          showErrorToast: false
        });
        
        localStorage.removeItem('wizard_state');
        localStorage.removeItem('wizard_backup');

        navigate(`/wizard-v2?claim=${job.id}`, {
          state: {
            freshDraftData: draftData.data || {},
            freshDraftStep: draftData.step || 3,
            draftId: job.id,
            fromDashboard: true
          }
        });
      } catch (err) {
        // Critical (audit 2026-04-25 spawn-fix): do NOT navigate to bare
        // /wizard-v2 here. useClaimId would see no ?claim= param and
        // POST /api/v1/claims to create a brand-new empty draft, leaving
        // the user with two cases that share a name. Forensic git showed
        // 4 duplicate "Test run" reports spawned this way over 3 days.
        console.error('Error fetching draft:', err);
        toast.error('Could not load that case. Please try again or refresh the page.');
      }
    } else {
      navigate(`/cleanup/validation/${job.id}`);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsUpdating(true);
    try {
      const token = await getToken();
      await api.patch(`/api/v1/reports/drafts/${job.id}/status`, 
        { workflow_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocalStatus(newStatus);
      onStatusChange?.();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setIsUpdating(false);
      setShowStatusDropdown(false);
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    setIsUpdating(true);
    try {
      const token = await getToken();
      await api.patch(`/api/v1/reports/drafts/${job.id}/status`, 
        { priority: newPriority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocalPriority(newPriority);
      onStatusChange?.();
    } catch (err) {
      console.error('Error updating priority:', err);
    } finally {
      setIsUpdating(false);
      setShowPriorityDropdown(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysAgoText = () => {
    if (job.days_inactive === undefined) return null;
    if (job.days_inactive === 0) return 'Today';
    if (job.days_inactive === 1) return '1 day ago';
    return `${job.days_inactive} days ago`;
  };

  // ---- table-row presentation (token-based, light + dark) ----
  const GRID = '2.3fr 1.4fr 1.9fr 1fr 0.95fr auto';
  const isComplete = Math.round(progressPercent) >= 100;
  const isStale = (job.days_inactive ?? 0) >= 7;

  // Status chip: Ready/complete → green ("✓"); Stalled/stale → orange ("‖"); else neutral ("▢")
  type ChipKind = 'done' | 'warn' | 'neutral';
  const chipKind: ChipKind =
    localStatus === 'ready' || isComplete
      ? 'done'
      : localStatus === 'stalled' || isStale
      ? 'warn'
      : 'neutral';
  const chipGlyph = chipKind === 'done' ? '✓' : chipKind === 'warn' ? '‖' : '▢';
  const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: '0.6875rem',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    border: '1px solid',
    ...(chipKind === 'done'
      ? { background: 'transparent', color: 'var(--color-verified)', borderColor: 'rgba(34,197,94,0.25)' }
      : chipKind === 'warn'
      ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'rgba(255,102,0,0.28)' }
      : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', borderColor: 'var(--color-border)' }),
  };

  return (
    <div
      className="cs-saved-row"
      data-testid={`saved-work-card-${job.id}`}
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 16,
        padding: '14px 22px',
        borderBottom: '1px solid var(--color-border-faint)',
        alignItems: 'center',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Claim */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            className="font-serif"
            style={{ fontSize: '0.875rem', color: 'var(--color-text-primary)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {job.client_name || job.business_name || 'Unnamed Claim'}
          </span>
          <span
            style={{ fontSize: '0.6562rem', color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {job.bank_name || 'Claim Wizard'}
          </span>
        </div>
        <div className="font-mono" style={{ fontSize: '0.6875rem', color: 'var(--color-text-ghost)', marginTop: 3 }}>
          {shortId}
        </div>
      </div>

      {/* Progress */}
      <div>
        <div style={{ height: 4, background: 'var(--color-border-sub)', borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: isComplete ? 'var(--color-verified)' : 'var(--color-brand)',
              borderRadius: 999,
              transition: 'width 0.3s',
            }}
          />
        </div>
        <div className="font-mono" style={{ fontSize: '0.6875rem', color: 'var(--color-text-dim)', marginTop: 6 }}>
          Step {job.step || 1}/{TOTAL_WIZARD_STEPS} · {Math.round(progressPercent)}%
        </div>
      </div>

      {/* Activity */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          <span className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>{job.doc_count || 0}</span> docs ·{' '}
          <span className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>{totalTxn.toLocaleString()}</span> txn
        </div>
        <div style={{ fontSize: '0.75rem', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--color-verified)' }}>
            <span className="font-mono">{autoApproved.toLocaleString()}</span> approved
          </span>
          {needsReview > 0 && (
            <span style={{ fontSize: '0.6562rem', fontWeight: 600, background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid rgba(255,102,0,0.28)', borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>
              {needsReview.toLocaleString()} review
            </span>
          )}
        </div>
      </div>

      {/* Updated */}
      <div>
        <div style={{ fontSize: '0.7812rem', color: 'var(--color-text-secondary)' }}>
          {formatDate(job.updated_at || job.saved_at)}
        </div>
        {job.days_inactive !== undefined && (
          <div
            className="font-mono"
            style={{ fontSize: '0.6875rem', marginTop: 3, color: isStale ? 'var(--color-needs-human-text)' : 'var(--color-text-dim)', fontWeight: isStale ? 600 : 400 }}
          >
            {getDaysAgoText()}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="relative">
        <button
          onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          style={{ ...chipStyle, cursor: 'pointer' }}
          data-testid={`status-badge-${job.id}`}
        >
          {chipGlyph} {statusConfig.label}
        </button>
        {showStatusDropdown && (
          <div className="absolute right-0 top-full mt-1 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg shadow-lg z-20 min-w-[160px]">
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={() => handleStatusChange(key)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-border-faint)] first:rounded-t-lg last:rounded-b-lg ${
                  localStatus === key ? 'bg-[var(--color-border-faint)] font-medium' : ''
                }`}
              >
                <span className={config.color}>{config.icon}</span>
                <span>{config.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        <button
          onClick={handleContinue}
          style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: 8, padding: '7px 15px', fontSize: '0.7812rem', fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
          data-testid={`draft-continue-${job.id}`}
        >
          Continue
        </button>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-dim)', fontSize: '0.8125rem', cursor: 'pointer', flex: '0 0 30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title="More actions"
            data-testid={`draft-menu-${job.id}`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg shadow-lg z-20 min-w-[180px] py-1">
              {/* Roadmap */}
              <button
                onClick={() => { setShowMenu(false); navigate(`/roadmap/${job.id}`); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-border-faint)]"
                data-testid={`draft-roadmap-${job.id}`}
              >
                <Map className="w-4 h-4 text-[var(--color-text-dim)]" />
                <span>View Roadmap</span>
              </button>

              {/* Status submenu */}
              <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wide text-[var(--color-text-dim)] font-semibold">Status</div>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => { setShowMenu(false); handleStatusChange(key); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[var(--color-border-faint)] ${
                    localStatus === key ? 'bg-[var(--color-border-faint)] font-medium' : ''
                  }`}
                >
                  <span className={config.color}>{config.icon}</span>
                  <span>{config.label}</span>
                </button>
              ))}

              {/* Priority submenu */}
              <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wide text-[var(--color-text-dim)] font-semibold">Priority</div>
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => { setShowMenu(false); handlePriorityChange(key); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[var(--color-border-faint)] ${
                    localPriority === key ? 'bg-[var(--color-border-faint)] font-medium' : ''
                  } ${config.color}`}
                  data-testid={`priority-btn-${job.id}`}
                >
                  <Flag className="w-3 h-3" />
                  <span className="capitalize">{key}</span>
                </button>
              ))}

              {/* Delete */}
              <div className="border-t border-[var(--color-border-faint)] mt-1 pt-1">
                <button
                  onClick={() => { setShowMenu(false); onDelete(job.id); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-needs-human-text)] hover:bg-[rgba(255,102,0,0.12)]"
                  data-testid={`draft-delete-${job.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete draft</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click-outside handler for menus */}
      {(showStatusDropdown || showPriorityDropdown || showMenu) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowStatusDropdown(false);
            setShowPriorityDropdown(false);
            setShowMenu(false);
          }}
        />
      )}

      {/* Loading overlay */}
      {isUpdating && (
        <div className="absolute inset-0 bg-[var(--color-bg-surface)]/50 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-brand)]" />
        </div>
      )}
    </div>
  );
}
